import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Offcanvas, 
    OffcanvasHeader, 
    OffcanvasBody, 
    Button, 
    FormGroup, 
    Label, 
    Input,
    Row,
    Col,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBriefcase,
    faCalendarDays,
    faUserGroup,
    faNoteSticky,
    faChevronLeft,
    faChevronRight,
    faCheck,
    faBell,
    faEnvelope,
    faMobileAlt,
    faCalendarPlus,
    faTruck,
    faUserMinus
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

import JobSelect from '../JobSelect';
import EmployeeSelect from '../EmployeeSelect';
import VehicleSelect from '../VehicleSelect';
import EquipmentSelect from '../EquipmentSelect';
import { CustomerSelect } from '../CustomerSelect';
import { selectAccountData } from '../../redux/main/accountSlice';
import { getNodeApiUrl } from '../../config/api.config';
import classnames from 'classnames';

const SchedulingOffcanvas = ({ 
    isOpen, 
    onClose, 
    selectedDate,
    onSuccess,
    // Props opzionali per preselezionare una commessa
    preselectedJob = null,
    initialTab = 'job' // 'job' o 'generic'
}) => {
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    
    // Tab attiva: 'job' per Pianificazione Commessa, 'generic' per Appuntamento Generico
    const [activeTab, setActiveTab] = useState(initialTab);
    
    // Step corrente (1-4)
    const [currentStep, setCurrentStep] = useState(1);
    
    // Step 1: Commessa
    const [selectedJob, setSelectedJob] = useState(preselectedJob);
    
    // Step 2: Schedulazione
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('17:00');
    
    // ===== STATI PER APPUNTAMENTO GENERICO =====
    const [genericTitle, setGenericTitle] = useState('');
    const [genericDescription, setGenericDescription] = useState('');
    const [genericStartDate, setGenericStartDate] = useState('');
    const [genericEndDate, setGenericEndDate] = useState('');
    const [genericStartTime, setGenericStartTime] = useState('08:00');
    const [genericEndTime, setGenericEndTime] = useState('17:00');
    const [genericEmployees, setGenericEmployees] = useState([]);
    const [genericCustomer, setGenericCustomer] = useState(null);
    const [genericSystem, setGenericSystem] = useState(null);
    const [genericSupplierId, setGenericSupplierId] = useState('');
    const [genericSupplierLabel, setGenericSupplierLabel] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [customerSystems, setCustomerSystems] = useState([]);
    const [isSubmittingGeneric, setIsSubmittingGeneric] = useState(false);
    
    // Notifiche appuntamento generico
    const [genericSendEmployeeEmail, setGenericSendEmployeeEmail] = useState(false);
    const [genericSendEmployeePush, setGenericSendEmployeePush] = useState(false);
    const [genericSendCustomerEmail, setGenericSendCustomerEmail] = useState(false);
    const [genericSendSupplierEmail, setGenericSendSupplierEmail] = useState(false);
    
    // ===== STATI PER ASSENZA =====
    const [absenceEmployee, setAbsenceEmployee] = useState(null);
    const [absenceType, setAbsenceType] = useState(null);
    const [absenceStartDate, setAbsenceStartDate] = useState('');
    const [absenceEndDate, setAbsenceEndDate] = useState('');
    const [absenceStartTime, setAbsenceStartTime] = useState('08:00');
    const [absenceEndTime, setAbsenceEndTime] = useState('17:00');
    const [absenceNotes, setAbsenceNotes] = useState('');
    const [isSubmittingAbsence, setIsSubmittingAbsence] = useState(false);
    
    // Step 3: Risorse
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [selectedEquipments, setSelectedEquipments] = useState([]);
    
    // Step 4: Note
    const [planningNotes, setPlanningNotes] = useState('');
    
    // Step 5: Notifiche
    const [sendCustomerEmail, setSendCustomerEmail] = useState(false);
    const [sendEmployeeEmail, setSendEmployeeEmail] = useState(false);
    const [sendEmployeePush, setSendEmployeePush] = useState(false);
    const [selectedCustomerEmails, setSelectedCustomerEmails] = useState([]); // Array di email selezionate
    
    // Modal conferma
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Definizione degli step
    const steps = [
        { number: 1, title: 'Commessa', icon: faBriefcase },
        { number: 2, title: 'Schedulazione', icon: faCalendarDays },
        { number: 3, title: 'Risorse', icon: faUserGroup },
        { number: 4, title: 'Note', icon: faNoteSticky },
        { number: 5, title: 'Notifiche', icon: faBell }
    ];
    
    // Reset form e imposta date quando si apre (modalità create)
    useEffect(() => {
        if (isOpen) {
            resetForm();
            // Imposta le date dalla selectedDate dopo il reset
            if (selectedDate) {
                const dateStr = moment(selectedDate).format('YYYY-MM-DD');
                setStartDate(dateStr);
                setEndDate(dateStr);
                setGenericStartDate(dateStr);
                setGenericEndDate(dateStr);
                setAbsenceStartDate(dateStr);
                setAbsenceEndDate(dateStr);
            }
            // Se c'è una commessa preselezionata, impostala
            if (preselectedJob) {
                setSelectedJob(preselectedJob);
                setActiveTab('job');
            }
            // Imposta il tab iniziale
            if (initialTab) {
                setActiveTab(initialTab);
            }
        }
    }, [isOpen, selectedDate, preselectedJob, initialTab]);
    
    // Cleanup del body scroll quando l'offcanvas si chiude
    useEffect(() => {
        if (!isOpen) {
            // Funzione di cleanup aggressiva
            const forceCleanup = () => {
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
                document.body.style.position = '';
                document.body.classList.remove('modal-open');
                document.body.classList.remove('offcanvas-open');
                document.documentElement.style.overflow = '';
                document.documentElement.style.paddingRight = '';
                // Rimuovi backdrop orfani in modo sicuro
                document.querySelectorAll('.offcanvas-backdrop, .modal-backdrop').forEach(el => {
                    if (el.parentNode) {
                        el.style.display = 'none';
                    }
                });
                // Reset anche su app-wrapper e app-content
                const appWrapper = document.querySelector('.app-wrapper');
                const appContent = document.querySelector('.app-content');
                if (appWrapper) {
                    appWrapper.style.overflow = '';
                    appWrapper.style.height = '';
                }
                if (appContent) {
                    appContent.style.overflow = '';
                    appContent.style.height = '';
                }
            };
            
            // Esegui cleanup multipli per sicurezza
            const t1 = setTimeout(forceCleanup, 100);
            const t2 = setTimeout(forceCleanup, 300);
            const t3 = setTimeout(forceCleanup, 500);
            const t4 = setTimeout(forceCleanup, 1000);
            
            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
                clearTimeout(t3);
                clearTimeout(t4);
            };
        }
    }, [isOpen]);
    
    // Stili CSS per z-index alto del backdrop
    useEffect(() => {
        const styleId = 'scheduling-offcanvas-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .scheduling-offcanvas-backdrop {
                    z-index: 200000 !important;
                }
                .scheduling-offcanvas {
                    z-index: 200001 !important;
                }
                .offcanvas.scheduling-offcanvas {
                    z-index: 200001 !important;
                }
            `;
            document.head.appendChild(style);
        }
        return () => {
            const styleElem = document.getElementById(styleId);
            if (styleElem) styleElem.remove();
        };
    }, []);
    
    const resetForm = () => {
        setCurrentStep(1);
        setSelectedJob(null);
        setStartDate('');
        setEndDate('');
        setStartTime('08:00');
        setEndTime('17:00');
        setSelectedEmployees([]);
        setSelectedVehicles([]);
        setSelectedEquipments([]);
        setPlanningNotes('');
        setSendCustomerEmail(false);
        setSendEmployeeEmail(false);
        setSendEmployeePush(false);
        setSelectedCustomerEmails([]);
        // Reset appuntamento generico
        setGenericTitle('');
        setGenericDescription('');
        setGenericStartDate('');
        setGenericEndDate('');
        setGenericStartTime('08:00');
        setGenericEndTime('17:00');
        setGenericEmployees([]);
        setGenericCustomer(null);
        setGenericSystem(null);
        setGenericSupplierId('');
        setGenericSupplierLabel('');
        setCustomerSystems([]);
        // Reset notifiche appuntamento generico
        setGenericSendEmployeeEmail(false);
        setGenericSendEmployeePush(false);
        setGenericSendCustomerEmail(false);
        setGenericSendSupplierEmail(false);
        // Reset assenza
        setAbsenceEmployee(null);
        setAbsenceType(null);
        setAbsenceStartDate('');
        setAbsenceEndDate('');
        setAbsenceStartTime('08:00');
        setAbsenceEndTime('17:00');
        setAbsenceNotes('');
    };
    
    // Carica i fornitori quando si apre l'offcanvas
    useEffect(() => {
        const fetchSuppliers = async () => {
            if (!isOpen || !accountData?._id) return;
            setLoadingSuppliers(true);
            try {
                const response = await axios.get(
                    getNodeApiUrl(`suppliers/suppliersList?accountId=${accountData._id}`),
                    { headers: { 'auth-token': token } }
                );
                if (response.data) {
                    setSuppliers(response.data);
                }
            } catch (error) {
                console.error('Errore caricamento fornitori:', error);
            } finally {
                setLoadingSuppliers(false);
            }
        };
        fetchSuppliers();
    }, [isOpen, accountData, token]);
    
    // Carica gli impianti quando viene selezionato un cliente
    useEffect(() => {
        const fetchSystems = async () => {
            if (!genericCustomer?.value || !accountData?._id) {
                setCustomerSystems([]);
                setGenericSystem(null);
                return;
            }
            try {
                const response = await axios.get(
                    getNodeApiUrl(`systems/systemsList?accountId=${accountData._id}&customerId=${genericCustomer.value}`),
                    { headers: { 'auth-token': token } }
                );
                if (Array.isArray(response.data)) {
                    setCustomerSystems(response.data);
                } else if (response.data?.data) {
                    setCustomerSystems(response.data.data);
                } else {
                    setCustomerSystems([]);
                }
            } catch (error) {
                console.error('Errore caricamento impianti:', error);
                setCustomerSystems([]);
            }
        };
        fetchSystems();
    }, [genericCustomer, accountData, token]);
    
    // Funzione per creare appuntamento generico
    const createGenericAppointment = async () => {
        // Validazione campi obbligatori
        if (!genericStartDate) {
            toast.warning('Seleziona una data di inizio');
            return;
        }
        if (!genericEndDate) {
            toast.warning('Seleziona una data di fine');
            return;
        }
        if (genericStartDate > genericEndDate) {
            toast.warning('La data di inizio deve essere precedente o uguale alla data di fine');
            return;
        }
        if (!genericStartTime || !genericEndTime) {
            toast.warning('Seleziona orario di inizio e fine');
            return;
        }
        if (genericStartDate === genericEndDate && genericStartTime >= genericEndTime) {
            toast.warning('L\'orario di inizio deve essere precedente all\'orario di fine');
            return;
        }
        
        setIsSubmittingGeneric(true);
        
        try {
            const accountId = accountData?._id;
            if (!accountId) {
                toast.error('Account non trovato');
                return;
            }
            
            // Costruisci le date complete con orari
            // Per appuntamento generico: un singolo evento che può coprire più giorni
            const fromDate = new Date(`${genericStartDate}T${genericStartTime}:00`);
            const toDate = new Date(`${genericEndDate}T${genericEndTime}:00`);
            
            // Calcola la durata in minuti
            const durationMs = toDate.getTime() - fromDate.getTime();
            const durationMinutes = Math.round(durationMs / 60000);
            
            // Calcola il timezone offset in minuti
            const timezoneOffset = new Date().getTimezoneOffset() * -1;
            
            // Costruisci il titolo
            const title = genericTitle || 'Appuntamento Generico';
            
            // Determina il colore in base ai collaboratori o usa un colore di default
            let eventColor = '#6c757d'; // Grigio di default per appuntamenti generici
            if (genericEmployees.length > 0 && genericEmployees[0].data?.color) {
                eventColor = genericEmployees[0].data.color;
            }
            
            // Prepara i dati dell'appuntamento
            const appointmentData = {
                accountId: accountId,
                appointment: {
                    jobId: null, // Nessuna commessa associata
                    jobCode: '',
                    customerId: genericCustomer?.value || '',
                    customerLabel: genericCustomer?.label || '',
                    systemId: genericSystem?._id || '',
                    systemLabel: genericSystem?.title || '',
                    systemAddressId: genericSystem?.customerAddressId || '',
                    systemAddressLabel: genericSystem?.customerAddressLabel || '',
                    supplierId: genericSupplierId || '',
                    supplierLabel: genericSupplierLabel || '',
                    fromDate: fromDate.toISOString(),
                    toDate: toDate.toISOString(),
                    duration: durationMinutes,
                    timezoneDiff: timezoneOffset,
                    users: genericEmployees.map(emp => ({
                        userId: emp.value,
                        userLabel: emp.label,
                        userTypeId: emp.data?.userTypeId || '',
                        userTypeLabel: emp.data?.userTypeLabel || '',
                        userMobile: emp.data?.mobile || '',
                        userEmail: emp.data?.email || '',
                        userColor: emp.data?.color || '#7367f0'
                    })),
                    vehicles: [],
                    equipments: [],
                    notes: genericDescription,
                    title: title,
                    type: 1, // Tipo 1 = Appuntamento Generico
                    isGenericAppointment: true,
                    active: true,
                    color: eventColor,
                    geoData: genericSystem?.geoData || {
                        type: 'Point',
                        coordinates: [0, 0],
                        distanceKm: 0,
                        distanceTime: 0
                    },
                    // Opzioni notifiche
                    notifications: {
                        sendEmployeeEmail: genericSendEmployeeEmail,
                        sendEmployeePush: genericSendEmployeePush,
                        sendCustomerEmail: genericSendCustomerEmail,
                        sendSupplierEmail: genericSendSupplierEmail
                    }
                }
            };
            
            // Chiamata API per creare l'appuntamento
            const response = await axios.post(
                getNodeApiUrl('appointments/create'),
                appointmentData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token
                    }
                }
            );
            
            if (response.data && (response.data._id || response.status === 200 || response.status === 201)) {
                toast.success('Appuntamento generico creato con successo!');
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                toast.error('Errore nella creazione dell\'appuntamento');
            }
        } catch (error) {
            console.error('Errore nella creazione dell\'appuntamento generico:', error);
            toast.error('Errore nella creazione: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsSubmittingGeneric(false);
        }
    };
    
    // Funzione per creare assenza
    const createAbsence = async () => {
        // Validazione campi obbligatori
        if (!absenceEmployee) {
            toast.warning('Seleziona un dipendente');
            return;
        }
        if (!absenceType) {
            toast.warning('Seleziona un tipo di assenza');
            return;
        }
        if (!absenceStartDate) {
            toast.warning('Seleziona una data di inizio');
            return;
        }
        if (!absenceEndDate) {
            toast.warning('Seleziona una data di fine');
            return;
        }
        if (absenceStartDate > absenceEndDate) {
            toast.warning('La data di inizio deve essere precedente o uguale alla data di fine');
            return;
        }
        if (absenceStartDate === absenceEndDate && absenceStartTime >= absenceEndTime) {
            toast.warning('L\'orario di inizio deve essere precedente all\'orario di fine');
            return;
        }
        
        setIsSubmittingAbsence(true);
        
        try {
            const accountId = accountData?._id;
            if (!accountId) {
                toast.error('Account non trovato');
                return;
            }
            
            // Calcoliamo l'offset del fuso orario locale in minuti
            const timezoneOffset = new Date().getTimezoneOffset() * -1;
            
            // Costruisci l'oggetto assenza
            const absencePayload = {
                userId: absenceEmployee.value,
                userLabel: absenceEmployee.label,
                absenceType: absenceType.value,
                absenceTypeId: absenceType._id,
                start: new Date(`${absenceStartDate}T${absenceStartTime}`).toISOString(),
                end: new Date(`${absenceEndDate}T${absenceEndTime}`).toISOString(),
                timezoneDiff: timezoneOffset,
                notes: absenceNotes || '',
                files: [],
                accountId: accountId
            };
            
            const response = await axios.post(
                getNodeApiUrl('/employeeAbsences'),
                absencePayload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token
                    }
                }
            );
            
            if (response.data && response.data.success) {
                toast.success('Assenza creata con successo');
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                toast.error('Errore nella creazione dell\'assenza');
            }
        } catch (error) {
            console.error('Errore nella creazione dell\'assenza:', error);
            let errorMessage = 'Si è verificato un errore';
            if (error.response?.data?.error?.message) {
                errorMessage = error.response.data.error.message;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(`Errore: ${errorMessage}`);
        } finally {
            setIsSubmittingAbsence(false);
        }
    };
    
    // Ottieni i tipi di assenza dall'account
    const absenceTypes = accountData?.settings?.employeeAbsenceTypes?.map(type => ({
        value: type.code,
        label: type.label,
        color: type.color,
        _id: type._id
    })) || [];
    
    // Navigazione step
    const goToNextStep = () => {
        // Validazione
        if (currentStep === 1 && !selectedJob) {
            toast.warning('Seleziona una commessa prima di procedere');
            return;
        }
        if (currentStep === 2) {
            if (!startDate || !endDate) {
                toast.warning('Seleziona data di inizio e fine');
                return;
            }
            if (startDate > endDate) {
                toast.warning('La data di inizio deve essere precedente o uguale alla data di fine');
                return;
            }
            if (startDate === endDate && startTime >= endTime) {
                toast.warning('L\'orario di inizio deve essere precedente all\'orario di fine');
                return;
            }
        }
        
        if (currentStep < 5) {
            setCurrentStep(currentStep + 1);
        }
    };
    
    const goToPreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };
    
    // Funzione per generare un array di date tra due date (incluse), inclusi i weekend
    const getDatesBetween = (start, end) => {
        // Caso speciale: se le date sono uguali
        if (start === end) {
            return [start];
        }
        
        const dateArray = [];
        let currentMoment = moment(start);
        const lastMoment = moment(end);
        
        // Aggiungiamo ogni data all'array (inclusi weekend)
        while (currentMoment.isSameOrBefore(lastMoment, 'day')) {
            const currentDate = currentMoment.format('YYYY-MM-DD');
            dateArray.push(currentDate);
            currentMoment.add(1, 'days');
        }
        
        return dateArray;
    };
    
    // Genera un ID univoco per raggruppare pianificazioni multiple
    const generateUniqueId = () => {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };
    
    // Mostra conferma prima di creare
    const handlePlanClick = () => {
        if (!selectedJob) {
            toast.error('Seleziona una commessa');
            setCurrentStep(1);
            return;
        }
        setShowConfirmModal(true);
    };
    
    // Crea la pianificazione (supporta multi-giorno)
    const createPlanning = async () => {
        setIsSubmitting(true);
        
        try {
            const accountId = accountData?._id;
            
            if (!accountId) {
                toast.error('Account non trovato');
                setIsSubmitting(false);
                return;
            }
            
            // Genera tutte le date tra startDate e endDate (inclusi weekend)
            const dates = getDatesBetween(startDate, endDate);
            
            if (dates.length === 0) {
                toast.warning('Nessuna data valida nel periodo selezionato');
                setIsSubmitting(false);
                setShowConfirmModal(false);
                return;
            }
            
            // Se c'è una sola data, usa l'endpoint singolo
            if (dates.length === 1) {
                // Costruisci le date complete con orari
                const fromDate = new Date(`${dates[0]}T${startTime}:00`);
                const toDate = new Date(`${dates[0]}T${endTime}:00`);
                
                await createSinglePlanning(accountId, fromDate, toDate);
            } else {
                // Se ci sono più date, crea pianificazioni multiple con createPlannings
                await createMultiplePlannings(accountId, dates);
            }
        } catch (error) {
            console.error('Errore nella creazione della pianificazione:', error);
            toast.error('Errore nella creazione: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Crea singola pianificazione
    const createSinglePlanning = async (accountId, fromDate, toDate) => {
        // Estrai i dati della commessa
        const jobData = selectedJob.data || {};
        
        // Calcola la durata in minuti
        const durationMs = toDate.getTime() - fromDate.getTime();
        const durationMinutes = Math.round(durationMs / 60000);
        
        // Calcola il timezone offset in minuti
        const timezoneOffset = new Date().getTimezoneOffset() * -1;
        
        // Costruisci il titolo completo
        const jobCode = jobData.code || '';
        const customerLabel = jobData.customerLabel || '';
        const jobTypeLabel = jobData.jobTypeLabel || '';
        const title = `${jobCode}${customerLabel ? ' - ' + customerLabel : ''}${jobTypeLabel ? ' (' + jobTypeLabel + ')' : ''} - Pianificata`;
        
        // Prepara i dati dell'appuntamento nel formato richiesto dall'API
        const appointmentData = {
            accountId: accountId,
            appointment: {
                jobId: selectedJob.value,
                jobCode: jobCode,
                jobTypeId: jobData.jobTypeId || '',
                jobTypeLabel: jobTypeLabel,
                customerId: jobData.customerId || '',
                customerLabel: customerLabel,
                assistanceRequestId: jobData.assistanceRequestId || '',
                assistanceRequestCode: jobData.assistanceRequestCode || '',
                maintenanceId: jobData.maintenanceId || '',
                maintenanceCode: jobData.maintenanceCode || '',
                systemId: jobData.systemId || '',
                systemLabel: jobData.systemLabel || selectedJob.systemLabel || '',
                systemAddressId: jobData.systemAddressId || '',
                systemAddressLabel: jobData.systemAddressLabel || selectedJob.systemAddressLabel || '',
                systemTypeList: jobData.systemTypeList || [],
                fromDate: fromDate.toISOString(),
                toDate: toDate.toISOString(),
                duration: durationMinutes,
                timezoneDiff: timezoneOffset,
                users: selectedEmployees.map(emp => ({
                    userId: emp.value,
                    userLabel: emp.label,
                    userTypeId: emp.data?.userTypeId || emp.userTypeId || '',
                    userTypeLabel: emp.data?.userTypeLabel || emp.userTypeLabel || '',
                    userMobile: emp.data?.mobile || emp.mobile || '',
                    userEmail: emp.data?.email || emp.email || '',
                    userColor: emp.data?.color || emp.color || '#7367f0'
                })),
                vehicles: selectedVehicles.map(v => ({
                    vehicleId: v.value,
                    vehicleLabel: v.label
                })),
                equipments: selectedEquipments.map(e => ({
                    equipmentId: e.value,
                    equipmentLabel: e.label
                })),
                notes: planningNotes,
                title: title,
                type: 0,
                active: true,
                notifications: {
                    sendCustomerEmail: sendCustomerEmail,
                    sendEmployeeEmail: sendEmployeeEmail,
                    sendEmployeePush: sendEmployeePush,
                    customerEmails: selectedCustomerEmails
                },
                geoData: jobData.geoData || {
                    type: 'Point',
                    coordinates: [0, 0],
                    distanceKm: 0,
                    distanceTime: 0
                }
            }
        };
        
        const response = await axios.post(
            getNodeApiUrl('appointments/create'),
            appointmentData,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            }
        );
        
        if (response.data && (response.data._id || response.status === 200 || response.status === 201)) {
            toast.success('Pianificazione creata con successo!');
            setShowConfirmModal(false);
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        } else {
            throw new Error('Errore nella creazione della pianificazione');
        }
    };
    
    // Crea pianificazioni multiple (per schedulazioni su più giorni)
    const createMultiplePlannings = async (accountId, dates) => {
        const jobData = selectedJob.data || {};
        const timezoneOffset = moment().utcOffset();
        const groupId = generateUniqueId();
        
        // Costruisci il titolo
        const jobCode = jobData.code || '';
        const customerLabel = jobData.customerLabel || '';
        const jobTypeLabel = jobData.jobTypeLabel || '';
        
        // Prepara le pianificazioni nel formato richiesto da createPlannings
        const plannings = [{
            id: generateUniqueId(),
            jobId: selectedJob.value,
            jobLabel: selectedJob.label,
            jobType: jobData.jobType || 'Altro',
            jobTypeLabel: jobTypeLabel,
            jobTypeAssistance: jobData.jobTypeAssistance || false,
            customerId: jobData.customerId || null,
            customerLabel: customerLabel,
            assistanceRequestId: jobData.assistanceRequestId || null,
            assistanceRequestCode: jobData.assistanceRequestCode || null,
            maintenanceId: jobData.maintenanceId || null,
            maintenanceCode: jobData.maintenanceCode || null,
            maintenanceActions: jobData.maintenanceActions || [],
            startDate: startDate,
            endDate: endDate,
            startTime: startTime,
            endTime: endTime,
            employees: selectedEmployees,
            vehicles: selectedVehicles,
            equipments: selectedEquipments,
            notes: planningNotes,
            dates: dates,
            groupId: groupId,
            timezoneDiff: timezoneOffset,
            _rawDates: true,
            // Dati extra per sistema
            systemId: jobData.systemId || '',
            systemLabel: jobData.systemLabel || '',
            systemAddressLabel: jobData.systemAddressLabel || '',
            geoData: jobData.geoData || null,
            // Notifiche
            notifications: {
                sendCustomerEmail: sendCustomerEmail,
                sendEmployeeEmail: sendEmployeeEmail,
                sendEmployeePush: sendEmployeePush,
                customerEmails: selectedCustomerEmails
            }
        }];
        
        const response = await axios.post(
            getNodeApiUrl('appointments/createPlannings'),
            {
                accountId,
                plannings: plannings
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            }
        );
        
        if (response.data && response.data.success) {
            const successCount = response.data.data?.success?.length || dates.length;
            const errorCount = response.data.data?.errors?.length || 0;
            
            if (errorCount === 0) {
                toast.success(`✅ ${successCount} pianificazioni create con successo!`);
            } else {
                toast.success(`✅ ${successCount} pianificazioni create`);
                toast.warning(`⚠️ ${errorCount} pianificazioni hanno avuto errori`);
            }
            
            setShowConfirmModal(false);
            onClose();
            if (onSuccess) {
                onSuccess();
            }
        } else {
            throw new Error('Errore nella creazione delle pianificazioni');
        }
    };
    
    // Render step indicator
    const renderStepIndicator = () => (
        <div className="d-flex justify-content-between mb-4 px-2">
            {steps.map((step, index) => (
                <div 
                    key={step.number}
                    className="d-flex flex-column align-items-center"
                    style={{ cursor: 'pointer', opacity: currentStep >= step.number ? 1 : 0.5 }}
                    onClick={() => {
                        if (step.number < currentStep) {
                            setCurrentStep(step.number);
                        }
                    }}
                >
                    <div 
                        className="rounded-circle d-flex align-items-center justify-content-center mb-1"
                        style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: currentStep >= step.number ? '#3182ce' : '#e2e8f0',
                            color: currentStep >= step.number ? 'white' : '#718096'
                        }}
                    >
                        <FontAwesomeIcon icon={step.icon} size="sm" />
                    </div>
                    <small className="text-center" style={{ fontSize: '0.7rem' }}>{step.title}</small>
                    {index < steps.length - 1 && (
                        <div 
                            style={{
                                position: 'absolute',
                                width: '50px',
                                height: '2px',
                                backgroundColor: currentStep > step.number ? '#3182ce' : '#e2e8f0',
                                top: '18px',
                                left: `calc(${(index + 1) * 25}% - 25px)`
                            }}
                        />
                    )}
                </div>
            ))}
        </div>
    );
    
    // Render contenuto step
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div>
                        <h6 className="mb-3">Seleziona la commessa da pianificare</h6>
                        <FormGroup>
                            <Label>Commessa</Label>
                            <JobSelect
                                selectedOption={selectedJob}
                                setSelectedOption={setSelectedJob}
                                placeholder="Cerca commessa..."
                            />
                        </FormGroup>
                        {selectedJob && (
                            <div className="mt-3 p-3 bg-light rounded">
                                <h6 className="mb-2">{selectedJob.data?.code || selectedJob.label?.split(' - ')[0] || 'N/D'}</h6>
                                <p className="mb-1"><strong>Cliente:</strong> {selectedJob.data?.customerLabel || selectedJob.label?.split(' - ')[1]?.split(' (')[0] || 'N/D'}</p>
                                <p className="mb-1"><strong>Tipo:</strong> {selectedJob.data?.jobTypeLabel || 'N/D'}</p>
                                {(selectedJob.data?.systemLabel || selectedJob.systemLabel) && (
                                    <p className="mb-0"><strong>Impianto:</strong> {selectedJob.data?.systemLabel || selectedJob.systemLabel}</p>
                                )}
                                {(selectedJob.data?.systemAddressLabel || selectedJob.systemAddressLabel) && (
                                    <p className="mb-0"><strong>Indirizzo:</strong> {selectedJob.data?.systemAddressLabel || selectedJob.systemAddressLabel}</p>
                                )}
                            </div>
                        )}
                    </div>
                );
            
            case 2:
                return (
                    <div>
                        <h6 className="mb-3">Imposta date e orari</h6>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Data Da <span className="text-danger">*</span></Label>
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            const newDate = e.target.value;
                                            setStartDate(newDate);
                                            // Se data fine è precedente, aggiornala
                                            if (newDate > endDate) {
                                                setEndDate(newDate);
                                            }
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Data A <span className="text-danger">*</span></Label>
                                    <Input
                                        type="date"
                                        value={endDate}
                                        min={startDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {startDate !== endDate && (
                            <div className="alert alert-info py-2 mb-3">
                                <small>
                                    <strong>Pianificazione multi-giorno:</strong> Verranno create pianificazioni separate per ogni giorno lavorativo (esclusi sabato e domenica)
                                </small>
                            </div>
                        )}
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Ora Inizio <span className="text-danger">*</span></Label>
                                    <Input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Ora Fine <span className="text-danger">*</span></Label>
                                    <Input
                                        type="time"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                );
            
            case 3:
                return (
                    <div>
                        <h6 className="mb-3">Assegna le risorse</h6>
                        <FormGroup>
                            <Label>Collaboratori</Label>
                            <EmployeeSelect
                                selectedOptions={selectedEmployees}
                                setSelectedOptions={setSelectedEmployees}
                                isMulti={true}
                                placeholder="Seleziona collaboratori..."
                                menuPlacement="bottom"
                                menuPortalTarget={null}
                                menuPosition="absolute"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Veicoli</Label>
                            <VehicleSelect
                                selectedOptions={selectedVehicles}
                                setSelectedOptions={setSelectedVehicles}
                                isMulti={true}
                                placeholder="Seleziona veicoli..."
                                menuPlacement="bottom"
                                menuPortalTarget={null}
                                menuPosition="absolute"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Attrezzature</Label>
                            <EquipmentSelect
                                selectedOptions={selectedEquipments}
                                setSelectedOptions={setSelectedEquipments}
                                isMulti={true}
                                placeholder="Seleziona attrezzature..."
                                menuPlacement="bottom"
                                menuPortalTarget={null}
                                menuPosition="absolute"
                            />
                        </FormGroup>
                    </div>
                );
            
            case 4:
                return (
                    <div>
                        <h6 className="mb-3">Note pianificazione</h6>
                        <FormGroup>
                            <Label>Note</Label>
                            <Input
                                type="textarea"
                                rows={4}
                                value={planningNotes}
                                onChange={(e) => setPlanningNotes(e.target.value)}
                                placeholder="Inserisci eventuali note..."
                            />
                        </FormGroup>
                    </div>
                );
            
            case 5:
                const jobData = selectedJob?.data || {};
                const customerEmails = jobData.customerEmails || [];
                const hasCustomerEmails = customerEmails.length > 0;
                
                // Gestione toggle email cliente
                const handleCustomerEmailToggle = (email, checked) => {
                    if (checked) {
                        setSelectedCustomerEmails(prev => [...prev, email]);
                    } else {
                        setSelectedCustomerEmails(prev => prev.filter(e => e !== email));
                    }
                };
                
                // Seleziona/deseleziona tutte le email
                const handleSelectAllEmails = (checked) => {
                    if (checked) {
                        setSelectedCustomerEmails(customerEmails.map(c => c.email));
                    } else {
                        setSelectedCustomerEmails([]);
                    }
                };
                
                return (
                    <div>
                        <h6 className="mb-3">Notifiche</h6>
                        
                        {/* Notifica Cliente */}
                        <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                    <strong>Email al Cliente</strong>
                                </div>
                                {hasCustomerEmails && customerEmails.length > 1 && (
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="selectAllEmails"
                                            checked={selectedCustomerEmails.length === customerEmails.length}
                                            onChange={(e) => handleSelectAllEmails(e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor="selectAllEmails">
                                            Tutti
                                        </label>
                                    </div>
                                )}
                            </div>
                            
                            {hasCustomerEmails ? (
                                <div className="mt-2">
                                    {customerEmails.map((contact, idx) => (
                                        <div key={idx} className="form-check mb-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`customerEmail_${idx}`}
                                                checked={selectedCustomerEmails.includes(contact.email)}
                                                onChange={(e) => handleCustomerEmailToggle(contact.email, e.target.checked)}
                                            />
                                            <label className="form-check-label small" htmlFor={`customerEmail_${idx}`}>
                                                <span className="fw-medium">{contact.name}</span>
                                                {contact.isMain && <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>principale</span>}
                                                <br />
                                                <span className="text-muted">{contact.email}</span>
                                            </label>
                                        </div>
                                    ))}
                                    {selectedCustomerEmails.length > 0 && (
                                        <small className="text-success d-block mt-2">
                                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                                            {selectedCustomerEmails.length} email selezionat{selectedCustomerEmails.length === 1 ? 'a' : 'e'}
                                        </small>
                                    )}
                                </div>
                            ) : (
                                <small className="text-warning">
                                    <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                                    Nessuna email associata al cliente
                                </small>
                            )}
                        </div>
                        
                        {/* Notifiche Collaboratori */}
                        <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                            <h6 className="mb-3">
                                <FontAwesomeIcon icon={faUserGroup} className="me-2 text-info" />
                                Notifiche ai Collaboratori
                            </h6>
                            
                            {selectedEmployees.length > 0 ? (
                                <>
                                    <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faMobileAlt} className="me-2 text-success" />
                                            <span>Notifica Push</span>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="sendEmployeePush"
                                                checked={sendEmployeePush}
                                                onChange={(e) => setSendEmployeePush(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                            <span>Email</span>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="sendEmployeeEmail"
                                                checked={sendEmployeeEmail}
                                                onChange={(e) => setSendEmployeeEmail(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                    <small className="text-muted d-block mt-2">
                                        Collaboratori: {selectedEmployees.map(e => e.label).join(', ')}
                                    </small>
                                </>
                            ) : (
                                <small className="text-warning">
                                    Nessun collaboratore assegnato
                                </small>
                            )}
                        </div>
                        
                        {/* Riepilogo */}
                        <div className="mt-4 p-3 bg-light rounded">
                            <h6 className="mb-3">Riepilogo Pianificazione</h6>
                            <p className="mb-1"><strong>Commessa:</strong> {selectedJob?.label || 'N/D'}</p>
                            
                            {/* Codice Assistenza se presente */}
                            {selectedJob?.data?.assistanceRequestCode && (
                                <p className="mb-1"><strong>Assistenza:</strong> {selectedJob.data.assistanceRequestCode}</p>
                            )}
                            
                            {/* Codice Manutenzione se presente */}
                            {selectedJob?.data?.maintenanceCode && (
                                <p className="mb-1"><strong>Manutenzione:</strong> {selectedJob.data.maintenanceCode}</p>
                            )}
                            
                            {/* Dati Impianto */}
                            {(selectedJob?.data?.systemLabel || selectedJob?.systemLabel) && (
                                <p className="mb-1"><strong>Impianto:</strong> {selectedJob?.data?.systemLabel || selectedJob?.systemLabel}</p>
                            )}
                            {(selectedJob?.data?.systemAddressLabel || selectedJob?.systemAddressLabel) && (
                                <p className="mb-1"><strong>Indirizzo:</strong> {selectedJob?.data?.systemAddressLabel || selectedJob?.systemAddressLabel}</p>
                            )}
                            {selectedJob?.data?.systemTypeList && selectedJob?.data?.systemTypeList.length > 0 && (
                                <p className="mb-1"><strong>Tipologie:</strong> {selectedJob.data.systemTypeList.map(t => t.typeLabel || t.label || t.name || '').filter(Boolean).join(', ')}</p>
                            )}
                            
                            <p className="mb-1"><strong>Data:</strong> {startDate === endDate ? moment(startDate).format('DD/MM/YYYY') : `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`}</p>
                            <p className="mb-1"><strong>Orario:</strong> {startTime} - {endTime}</p>
                            <p className="mb-1"><strong>Collaboratori:</strong> {selectedEmployees.length > 0 ? selectedEmployees.map(e => e.label).join(', ') : 'Nessuno'}</p>
                            <p className="mb-1"><strong>Veicoli:</strong> {selectedVehicles.length > 0 ? selectedVehicles.map(v => v.label).join(', ') : 'Nessuno'}</p>
                            <p className="mb-1"><strong>Attrezzature:</strong> {selectedEquipments.length > 0 ? selectedEquipments.map(e => e.label).join(', ') : 'Nessuna'}</p>
                            
                            {/* Note */}
                            {planningNotes && (
                                <div className="mt-2 pt-2 border-top">
                                    <p className="mb-0"><strong>Note:</strong> {planningNotes}</p>
                                </div>
                            )}
                            
                            {(selectedCustomerEmails.length > 0 || sendEmployeeEmail || sendEmployeePush) && (
                                <div className="mt-2 pt-2 border-top">
                                    <strong>Notifiche:</strong>
                                    <ul className="mb-0 ps-3">
                                        {selectedCustomerEmails.length > 0 && <li>Email cliente: {selectedCustomerEmails.join(', ')}</li>}
                                        {sendEmployeePush && <li>Push ai collaboratori</li>}
                                        {sendEmployeeEmail && <li>Email ai collaboratori</li>}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };
    
    return (
        <>
            <Offcanvas
                isOpen={isOpen}
                toggle={onClose}
                direction="end"
                style={{ width: '45%', minWidth: '550px', zIndex: 200001 }}
                backdropClassName="scheduling-offcanvas-backdrop"
                className="scheduling-offcanvas"
                unmountOnClose={true}
                returnFocusAfterClose={false}
                scrollable={true}
                backdrop={true}
            >
                <OffcanvasHeader toggle={onClose}>
                    <FontAwesomeIcon icon={faCalendarDays} className="me-2" />
                    Nuovo Evento - {selectedDate ? moment(selectedDate).format('DD/MM/YYYY') : ''}
                </OffcanvasHeader>
                <OffcanvasBody>
                    {/* Tab di navigazione - Stile dettaglio commessa */}
                    <Nav 
                        className="mb-3" 
                        style={{ 
                            borderBottom: '1px solid #dee2e6',
                            gap: '0'
                        }}
                    >
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'job' })}
                                onClick={() => setActiveTab('job')}
                                style={{ 
                                    cursor: 'pointer',
                                    border: 'none',
                                    borderBottom: activeTab === 'job' ? '3px solid #343a40' : '3px solid transparent',
                                    borderRadius: 0,
                                    color: activeTab === 'job' ? '#343a40' : '#6c757d',
                                    fontWeight: activeTab === 'job' ? '600' : '500',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.5px',
                                    padding: '12px 16px',
                                    backgroundColor: 'transparent',
                                    marginBottom: '-1px'
                                }}
                            >
                                PIANIFICAZIONE
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'generic' })}
                                onClick={() => setActiveTab('generic')}
                                style={{ 
                                    cursor: 'pointer',
                                    border: 'none',
                                    borderBottom: activeTab === 'generic' ? '3px solid #343a40' : '3px solid transparent',
                                    borderRadius: 0,
                                    color: activeTab === 'generic' ? '#343a40' : '#6c757d',
                                    fontWeight: activeTab === 'generic' ? '600' : '500',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.5px',
                                    padding: '12px 16px',
                                    backgroundColor: 'transparent',
                                    marginBottom: '-1px'
                                }}
                            >
                                APPUNTAMENTO
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'absence' })}
                                onClick={() => setActiveTab('absence')}
                                style={{ 
                                    cursor: 'pointer',
                                    border: 'none',
                                    borderBottom: activeTab === 'absence' ? '3px solid #343a40' : '3px solid transparent',
                                    borderRadius: 0,
                                    color: activeTab === 'absence' ? '#343a40' : '#6c757d',
                                    fontWeight: activeTab === 'absence' ? '600' : '500',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.5px',
                                    padding: '12px 16px',
                                    backgroundColor: 'transparent',
                                    marginBottom: '-1px'
                                }}
                            >
                                ASSENZA
                            </NavLink>
                        </NavItem>
                    </Nav>
                    
                    <TabContent activeTab={activeTab}>
                        {/* TAB: Pianificazione Commessa */}
                        <TabPane tabId="job">
                            <div className="alert alert-light py-2 mb-3" style={{ fontSize: '0.85rem', border: '1px solid #e9ecef' }}>
                                <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
                                Con questa pianificazione i collaboratori potranno caricare ore sulla commessa
                            </div>
                            {renderStepIndicator()}
                            
                            <div style={{ minHeight: '300px' }}>
                                {renderStepContent()}
                            </div>
                            
                            {/* Pulsanti navigazione */}
                            <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                                <Button
                                    color="secondary"
                                    outline
                                    onClick={goToPreviousStep}
                                    disabled={currentStep === 1}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                                    Indietro
                                </Button>
                                
                                {currentStep < 5 ? (
                                    <Button color="primary" onClick={goToNextStep}>
                                        Avanti
                                        <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                                    </Button>
                                ) : (
                                    <Button color="success" onClick={handlePlanClick}>
                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                        Pianifica
                                    </Button>
                                )}
                            </div>
                        </TabPane>
                        
                        {/* TAB: Appuntamento Generico */}
                        <TabPane tabId="generic">
                            <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                                <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                                Creando questo appuntamento i collaboratori <strong>NON</strong> potranno caricare ore
                            </div>
                            <div style={{ minHeight: '300px' }}>
                                {/* Titolo */}
                                <FormGroup>
                                    <Label>Titolo</Label>
                                    <Input
                                        type="text"
                                        value={genericTitle}
                                        onChange={(e) => setGenericTitle(e.target.value)}
                                        placeholder="Es: Riunione, Sopralluogo, ecc..."
                                    />
                                </FormGroup>
                                
                                {/* Date */}
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Data Da <span className="text-danger">*</span></Label>
                                            <Input
                                                type="date"
                                                value={genericStartDate}
                                                onChange={(e) => {
                                                    const newDate = e.target.value;
                                                    setGenericStartDate(newDate);
                                                    // Se data fine è precedente, aggiornala
                                                    if (newDate > genericEndDate) {
                                                        setGenericEndDate(newDate);
                                                    }
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Data A <span className="text-danger">*</span></Label>
                                            <Input
                                                type="date"
                                                value={genericEndDate}
                                                min={genericStartDate}
                                                onChange={(e) => setGenericEndDate(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                
                                {/* Orari */}
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Dalle <span className="text-danger">*</span></Label>
                                            <Input
                                                type="time"
                                                value={genericStartTime}
                                                onChange={(e) => setGenericStartTime(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Alle <span className="text-danger">*</span></Label>
                                            <Input
                                                type="time"
                                                value={genericEndTime}
                                                onChange={(e) => setGenericEndTime(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                
                                {/* Collaboratori */}
                                <FormGroup>
                                    <Label>Collaboratori</Label>
                                    <EmployeeSelect
                                        selectedOptions={genericEmployees}
                                        setSelectedOptions={setGenericEmployees}
                                        isMulti={true}
                                        placeholder="Seleziona collaboratori..."
                                        menuPlacement="bottom"
                                        menuPortalTarget={null}
                                        menuPosition="absolute"
                                    />
                                </FormGroup>
                                
                                {/* Cliente */}
                                <FormGroup>
                                    <Label>Cliente</Label>
                                    <CustomerSelect
                                        selectedOption={genericCustomer}
                                        setSelectedOption={setGenericCustomer}
                                        placeholder="Cerca cliente..."
                                        showSelectedLabel={false}
                                    />
                                </FormGroup>
                                
                                {/* Impianto (visibile solo se è selezionato un cliente) */}
                                {genericCustomer && (
                                    <FormGroup>
                                        <Label>Impianto</Label>
                                        <Input
                                            type="select"
                                            value={genericSystem?._id || ''}
                                            onChange={(e) => {
                                                const system = customerSystems.find(s => s._id === e.target.value);
                                                setGenericSystem(system || null);
                                            }}
                                        >
                                            <option value="">Seleziona impianto...</option>
                                            {customerSystems.map(system => (
                                                <option key={system._id} value={system._id}>
                                                    {system.title} - {system.customerAddressLabel || 'Indirizzo non specificato'}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                )}
                                
                                {/* Fornitore - temporaneamente nascosto
                                <FormGroup>
                                    <Label>Fornitore</Label>
                                    <Input
                                        type="select"
                                        value={genericSupplierId}
                                        onChange={(e) => {
                                            const supplier = suppliers.find(s => s._id === e.target.value);
                                            setGenericSupplierId(e.target.value);
                                            setGenericSupplierLabel(supplier?.company || supplier?.name || '');
                                        }}
                                        disabled={loadingSuppliers}
                                    >
                                        <option value="">Seleziona fornitore...</option>
                                        {suppliers.map(supplier => (
                                            <option key={supplier._id} value={supplier._id}>
                                                {supplier.company || supplier.name || supplier.code}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                                */}
                                
                                {/* Descrizione */}
                                <FormGroup>
                                    <Label>Descrizione</Label>
                                    <Input
                                        type="textarea"
                                        rows={3}
                                        value={genericDescription}
                                        onChange={(e) => setGenericDescription(e.target.value)}
                                        placeholder="Note o descrizione dell'appuntamento..."
                                    />
                                </FormGroup>
                                
                                {/* Sezione Notifiche - visibile solo se ci sono destinatari */}
                                {(genericEmployees.length > 0 || genericCustomer || genericSupplierId) && (
                                    <>
                                        <h6 className="mb-3 mt-4">Notifiche</h6>
                                        
                                        {/* Notifica Cliente */}
                                        {genericCustomer && (
                                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                                        <strong>Email al Cliente</strong>
                                                    </div>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="genericCustomerEmail"
                                                            checked={genericSendCustomerEmail}
                                                            onChange={(e) => setGenericSendCustomerEmail(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Notifica Fornitore */}
                                        {genericSupplierId && (
                                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-warning" />
                                                        <strong>Email al Fornitore</strong>
                                                    </div>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="genericSupplierEmail"
                                                            checked={genericSendSupplierEmail}
                                                            onChange={(e) => setGenericSendSupplierEmail(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                                <small className="text-muted">
                                                    Fornitore: {genericSupplierLabel || 'N/D'}
                                                </small>
                                            </div>
                                        )}
                                        
                                        {/* Notifiche Collaboratori */}
                                        {genericEmployees.length > 0 && (
                                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                                <h6 className="mb-3">
                                                    <FontAwesomeIcon icon={faUserGroup} className="me-2 text-info" />
                                                    Notifiche ai Collaboratori
                                                </h6>
                                                
                                                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faMobileAlt} className="me-2 text-success" />
                                                        <span>Notifica Push</span>
                                                    </div>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="genericEmployeePush"
                                                            checked={genericSendEmployeePush}
                                                            onChange={(e) => setGenericSendEmployeePush(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                                        <span>Email</span>
                                                    </div>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="genericEmployeeEmail"
                                                            checked={genericSendEmployeeEmail}
                                                            onChange={(e) => setGenericSendEmployeeEmail(e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                                <small className="text-muted d-block mt-2">
                                                    Collaboratori: {genericEmployees.map(e => e.label).join(', ')}
                                                </small>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            
                            {/* Pulsante Crea Appuntamento */}
                            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                <Button 
                                    color="success" 
                                    onClick={createGenericAppointment}
                                    disabled={isSubmittingGeneric}
                                >
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    {isSubmittingGeneric ? 'Creazione...' : 'Crea Appuntamento'}
                                </Button>
                            </div>
                        </TabPane>
                        
                        {/* TAB: Assenza */}
                        <TabPane tabId="absence">
                            <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                                <FontAwesomeIcon icon={faUserMinus} className="me-2" />
                                Registra un'assenza per un dipendente (ferie, malattia, permesso, ecc.)
                            </div>
                            <div style={{ minHeight: '300px' }}>
                                {/* Dipendente */}
                                <FormGroup>
                                    <Label>Dipendente <span className="text-danger">*</span></Label>
                                    <EmployeeSelect
                                        selectedOptions={absenceEmployee}
                                        setSelectedOptions={setAbsenceEmployee}
                                        isMulti={false}
                                        placeholder="Seleziona dipendente..."
                                        menuPlacement="bottom"
                                        menuPortalTarget={null}
                                        menuPosition="absolute"
                                    />
                                </FormGroup>
                                
                                {/* Tipo Assenza */}
                                <FormGroup>
                                    <Label>Tipo Assenza <span className="text-danger">*</span></Label>
                                    <Select
                                        placeholder="Seleziona tipo assenza"
                                        isClearable={true}
                                        options={absenceTypes}
                                        value={absenceType}
                                        onChange={(selected) => setAbsenceType(selected)}
                                        styles={{
                                            control: (baseStyles) => ({
                                                ...baseStyles,
                                                borderColor: '#ced4da',
                                                fontSize: '0.95rem'
                                            }),
                                            option: (baseStyles, { data, isSelected }) => ({
                                                ...baseStyles,
                                                color: isSelected ? '#fff' : '#333',
                                                backgroundColor: isSelected ? data.color : baseStyles.backgroundColor,
                                                ':hover': {
                                                    ...baseStyles[':hover'],
                                                    backgroundColor: isSelected ? data.color : `${data.color}22`
                                                }
                                            })
                                        }}
                                        classNamePrefix="select"
                                    />
                                </FormGroup>
                                
                                {/* Date */}
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Data Inizio <span className="text-danger">*</span></Label>
                                            <Input
                                                type="date"
                                                value={absenceStartDate}
                                                onChange={(e) => {
                                                    const newDate = e.target.value;
                                                    setAbsenceStartDate(newDate);
                                                    if (newDate > absenceEndDate) {
                                                        setAbsenceEndDate(newDate);
                                                    }
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Data Fine <span className="text-danger">*</span></Label>
                                            <Input
                                                type="date"
                                                value={absenceEndDate}
                                                min={absenceStartDate}
                                                onChange={(e) => setAbsenceEndDate(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                
                                {/* Orari */}
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Ora Inizio</Label>
                                            <Input
                                                type="time"
                                                value={absenceStartTime}
                                                onChange={(e) => setAbsenceStartTime(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Ora Fine</Label>
                                            <Input
                                                type="time"
                                                value={absenceEndTime}
                                                onChange={(e) => setAbsenceEndTime(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                
                                {/* Note */}
                                <FormGroup>
                                    <Label>Note</Label>
                                    <Input
                                        type="textarea"
                                        rows={3}
                                        value={absenceNotes}
                                        onChange={(e) => setAbsenceNotes(e.target.value)}
                                        placeholder="Inserisci eventuali note..."
                                    />
                                </FormGroup>
                            </div>
                            
                            {/* Pulsante Crea Assenza */}
                            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                <Button 
                                    color="success" 
                                    onClick={createAbsence}
                                    disabled={isSubmittingAbsence}
                                >
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    {isSubmittingAbsence ? 'Creazione...' : 'Crea Assenza'}
                                </Button>
                            </div>
                        </TabPane>
                    </TabContent>
                </OffcanvasBody>
            </Offcanvas>
            
            {/* Modal di conferma */}
            <Modal 
                isOpen={showConfirmModal} 
                toggle={() => setShowConfirmModal(false)}
                unmountOnClose={true}
                returnFocusAfterClose={false}
                style={{ zIndex: 200010 }}
            >
                <ModalHeader toggle={() => setShowConfirmModal(false)}>
                    Conferma Pianificazione
                </ModalHeader>
                <ModalBody>
                    <p>Sei sicuro di voler creare questa pianificazione?</p>
                    <div className="p-3 bg-light rounded">
                        <p className="mb-1"><strong>Commessa:</strong> {selectedJob?.label}</p>
                        <p className="mb-1"><strong>Data:</strong> {startDate === endDate ? moment(startDate).format('DD/MM/YYYY') : `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`}</p>
                        <p className="mb-0"><strong>Orario:</strong> {startTime} - {endTime}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowConfirmModal(false)} disabled={isSubmitting}>
                        Annulla
                    </Button>
                    <Button color="success" onClick={createPlanning} disabled={isSubmitting}>
                        {isSubmitting ? 'Creazione...' : 'Conferma'}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default SchedulingOffcanvas;
